import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  PageBreak,
  Header,
  Footer,
  PageNumber,
  NumberFormat,
  convertInchesToTwip,
} from 'docx';
import type { Project, ExportOptions, Chapter } from '../types';

// Strip HTML tags and convert to plain text
function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

// Convert HTML paragraphs to docx paragraphs
function htmlToParagraphs(html: string, options: ExportOptions): Paragraph[] {
  const paragraphs: Paragraph[] = [];
  const text = stripHtml(html);
  const parts = text.split(/\n\n+/);

  const fontSize = options.fontSize === 'small' ? 20 : options.fontSize === 'large' ? 24 : 22;

  parts.forEach((part, index) => {
    if (part.trim()) {
      paragraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: part.trim(),
              size: fontSize,
              font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
            }),
          ],
          spacing: {
            line: options.lineSpacing === 'single' ? 240 : options.lineSpacing === 'double' ? 480 : 360,
            after: 200,
          },
          indent: index > 0 ? { firstLine: convertInchesToTwip(0.5) } : undefined,
        })
      );
    }
  });

  return paragraphs;
}

// Get page margins based on options
function getMargins(options: ExportOptions) {
  const marginSizes = {
    narrow: { top: 720, right: 720, bottom: 720, left: 720 },
    normal: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
    wide: { top: 1800, right: 1800, bottom: 1800, left: 1800 },
  };
  return marginSizes[options.margins];
}

// Create title page
function createTitlePage(project: Project, options: ExportOptions): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  // Add spacing at top
  paragraphs.push(
    new Paragraph({
      children: [],
      spacing: { before: 4000 },
    })
  );

  // Title
  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: project.metadata.title || 'Untitled',
          bold: true,
          size: 56,
          font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    })
  );

  // Subtitle
  if (project.metadata.subtitle) {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: project.metadata.subtitle,
            italics: true,
            size: 32,
            font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 800 },
      })
    );
  }

  // Author
  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: project.metadata.author || '',
          size: 28,
          font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 2000 },
    })
  );

  return paragraphs;
}

// Create copyright page
function createCopyrightPage(project: Project, options: ExportOptions): Paragraph[] {
  const paragraphs: Paragraph[] = [];
  const year = new Date().getFullYear();
  const fontSize = options.fontSize === 'small' ? 18 : options.fontSize === 'large' ? 22 : 20;

  paragraphs.push(
    new Paragraph({
      children: [],
      pageBreakBefore: true,
    })
  );

  paragraphs.push(
    new Paragraph({
      children: [],
      spacing: { before: 8000 },
    })
  );

  const copyrightText = project.metadata.copyright ||
    `Copyright © ${year} ${project.metadata.author || 'Author'}\n\nAll rights reserved. No part of this publication may be reproduced, distributed, or transmitted in any form or by any means without the prior written permission of the publisher.`;

  copyrightText.split('\n\n').forEach(text => {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: text.trim(),
            size: fontSize,
            font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      })
    );
  });

  if (project.metadata.isbn) {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `ISBN: ${project.metadata.isbn}`,
            size: fontSize,
            font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { before: 400 },
      })
    );
  }

  return paragraphs;
}

// Create table of contents
function createTocPage(project: Project, options: ExportOptions): Paragraph[] {
  const paragraphs: Paragraph[] = [];
  const fontSize = options.fontSize === 'small' ? 20 : options.fontSize === 'large' ? 24 : 22;

  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Contents',
          bold: true,
          size: 36,
          font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
        }),
      ],
      alignment: AlignmentType.CENTER,
      pageBreakBefore: true,
      spacing: { after: 800 },
    })
  );

  project.chapters.forEach((chapter, index) => {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: chapter.title || `Chapter ${index + 1}`,
            size: fontSize,
            font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
          }),
        ],
        spacing: { after: 200 },
      })
    );
  });

  return paragraphs;
}

// Create chapter
function createChapter(chapter: Chapter, index: number, options: ExportOptions): Paragraph[] {
  const paragraphs: Paragraph[] = [];
  const headingSize = options.fontSize === 'small' ? 28 : options.fontSize === 'large' ? 36 : 32;

  // Chapter heading
  paragraphs.push(
    new Paragraph({
      children: [
        new TextRun({
          text: chapter.title || `Chapter ${index + 1}`,
          bold: true,
          size: headingSize,
          font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
        }),
      ],
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      pageBreakBefore: true,
      spacing: { before: 2000, after: 800 },
    })
  );

  // Subtitle
  if (chapter.subtitle) {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: chapter.subtitle,
            italics: true,
            size: headingSize - 6,
            font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 800 },
      })
    );
  }

  // Chapter content
  const contentParagraphs = htmlToParagraphs(chapter.content, options);
  paragraphs.push(...contentParagraphs);

  return paragraphs;
}

export async function exportToDocx(project: Project, options: ExportOptions): Promise<Blob> {
  const sections: Paragraph[] = [];

  // Title page
  if (options.includeTitle) {
    sections.push(...createTitlePage(project, options));
  }

  // Copyright page
  if (options.includeCopyright) {
    sections.push(...createCopyrightPage(project, options));
  }

  // Table of contents
  if (options.includeToc) {
    sections.push(...createTocPage(project, options));
  }

  // Chapters
  project.chapters.forEach((chapter, index) => {
    sections.push(...createChapter(chapter, index, options));
  });

  const margins = getMargins(options);

  // Create document
  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: margins,
          },
        },
        headers: options.headerFooter !== 'none' ? {
          default: new Header({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: options.headerFooter === 'title'
                      ? project.metadata.title || ''
                      : options.headerFooter === 'author'
                        ? project.metadata.author || ''
                        : '',
                    size: 18,
                    font: options.fontFamily === 'serif' ? 'Georgia' : 'Arial',
                  }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        } : undefined,
        footers: options.includePageNumbers ? {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: 18,
                  }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
          }),
        } : undefined,
        children: sections,
      },
    ],
  });

  return await Packer.toBlob(doc);
}
